import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Globe, 
  Award, 
  BookOpen, 
  CheckCircle, 
  ArrowRight, 
  Mail, 
  Phone,
  MessageCircle,
  Star,
  ExternalLink,
  FileText,
  Users,
  Shield
} from "lucide-react";

export default function IndexedPublications() {
  const whatsappLink = "https://wa.me/916284607500";

  return (
    <div data-testid="indexed-publications-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da?crop=entropy&cs=srgb&fm=jpg&w=1920" 
            alt="Academic Library"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Star className="w-6 h-6 text-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Premium Research Publication</span>
              <Star className="w-6 h-6 text-[#C5A059]" />
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-medium text-white mb-6">
              SCOPUS | WOS | ABDC
            </h1>
            <p className="text-slate-300 text-lg mb-4 max-w-2xl mx-auto">
              Peer-Reviewed & Indexed Journal Publications
            </p>
            <p className="text-slate-400 max-w-3xl mx-auto">
              Get your research published in prestigious indexed journals with our expert guidance and support. We assist researchers in publishing their work in high-impact, peer-reviewed journals.
            </p>
          </div>
        </div>
      </section>

      {/* Index Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Publication Indexes We Cover</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* SCOPUS */}
            <Card className="bg-white border-2 border-slate-100 rounded-none hover:border-[#C5A059] transition-colors" data-testid="scopus-info-card">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center mx-auto mb-6 rounded-lg">
                  <Globe className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">SCOPUS</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Elsevier's abstract and citation database covering 27,000+ peer-reviewed journals.
                </p>
                <ul className="text-left space-y-2 text-sm text-slate-500">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> High citation impact</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> Global recognition</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> CiteScore metrics</li>
                </ul>
              </CardContent>
            </Card>

            {/* Web of Science */}
            <Card className="bg-white border-2 border-slate-100 rounded-none hover:border-[#C5A059] transition-colors" data-testid="wos-info-card">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center mx-auto mb-6 rounded-lg">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">Web of Science</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Clarivate's premier citation indexing service covering 21,000+ journals.
                </p>
                <ul className="text-left space-y-2 text-sm text-slate-500">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> SCI/SCIE indexed</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> SSCI indexed</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> Impact Factor</li>
                </ul>
              </CardContent>
            </Card>

            {/* ABDC */}
            <Card className="bg-white border-2 border-slate-100 rounded-none hover:border-[#C5A059] transition-colors" data-testid="abdc-info-card">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mx-auto mb-6 rounded-lg">
                  <BookOpen className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">ABDC Listed</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Australian Business Deans Council journal quality rankings.
                </p>
                <ul className="text-left space-y-2 text-sm text-slate-500">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> A* ranked journals</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> A, B, C categories</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> Business focus</li>
                </ul>
              </CardContent>
            </Card>

            {/* Peer Reviewed */}
            <Card className="bg-white border-2 border-slate-100 rounded-none hover:border-[#C5A059] transition-colors" data-testid="peer-reviewed-info-card">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mx-auto mb-6 rounded-lg">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">Peer Reviewed</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Quality assured through rigorous peer review process.
                </p>
                <ul className="text-left space-y-2 text-sm text-slate-500">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> Expert review</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> Quality assured</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> UGC CARE listed</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Active Journals 2026 */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">2026 Active Journals</span>
            <h2 className="font-serif text-4xl font-medium text-slate-900 mt-3 mb-6">Indexed Journals We Support</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 max-w-2xl mx-auto">
              We assist researchers in publishing their work in the following prestigious indexed journals
            </p>
          </div>

          {/* SCOPUS Journals */}
          <div className="mb-12">
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 flex items-center justify-center rounded">
                <Globe className="w-5 h-5 text-white" />
              </div>
              SCOPUS Indexed Journals
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <JournalCard name="International Journal of Law Management & Humanities" issn="2581-5369" index="SCOPUS" category="Law/Management" />
              <JournalCard name="Journal of Emerging Technologies and Innovative Research" issn="2349-5162" index="SCOPUS" category="Technology" />
              <JournalCard name="International Journal of Creative Research Thoughts" issn="2320-2882" index="SCOPUS" category="Multidisciplinary" />
              <JournalCard name="International Journal of Research Publication and Reviews" issn="2582-7421" index="SCOPUS" category="Multidisciplinary" />
              <JournalCard name="Journal of Pharmaceutical Negative Results" issn="0976-9234" index="SCOPUS" category="Pharmaceutical" />
              <JournalCard name="International Journal of Health Sciences" issn="2550-6978" index="SCOPUS" category="Health Sciences" />
              <JournalCard name="Journal of Xi'an University of Architecture & Technology" issn="1006-7930" index="SCOPUS" category="Engineering" />
              <JournalCard name="Palarch's Journal of Archaeology of Egypt/Egyptology" issn="1567-214X" index="SCOPUS" category="Archaeology" />
              <JournalCard name="International Journal of Psychosocial Rehabilitation" issn="1475-7192" index="SCOPUS" category="Psychology" />
            </div>
          </div>

          {/* WOS Journals */}
          <div className="mb-12">
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 flex items-center justify-center rounded">
                <Award className="w-5 h-5 text-white" />
              </div>
              Web of Science (WOS) Indexed Journals
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <JournalCard name="Journal of Business Research" issn="0148-2963" index="WOS/SCIE" category="Business" impactFactor="11.3" />
              <JournalCard name="Computers in Human Behavior" issn="0747-5632" index="WOS/SSCI" category="Psychology/IT" impactFactor="9.9" />
              <JournalCard name="Journal of Cleaner Production" issn="0959-6526" index="WOS/SCIE" category="Environmental" impactFactor="11.1" />
              <JournalCard name="Technological Forecasting and Social Change" issn="0040-1625" index="WOS/SSCI" category="Technology" impactFactor="12.9" />
              <JournalCard name="Journal of Environmental Management" issn="0301-4797" index="WOS/SCIE" category="Environmental" impactFactor="8.7" />
              <JournalCard name="Resources Policy" issn="0301-4207" index="WOS/SSCI" category="Economics" impactFactor="10.2" />
              <JournalCard name="International Journal of Information Management" issn="0268-4012" index="WOS/SSCI" category="IT/Management" impactFactor="21.0" />
              <JournalCard name="Journal of Retailing and Consumer Services" issn="0969-6989" index="WOS/SSCI" category="Marketing" impactFactor="10.4" />
              <JournalCard name="Sustainable Cities and Society" issn="2210-6707" index="WOS/SCIE" category="Urban Studies" impactFactor="11.7" />
            </div>
          </div>

          {/* ABDC Journals */}
          <div className="mb-12">
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 flex items-center justify-center rounded">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              ABDC Listed Journals
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <JournalCard name="Academy of Management Journal" issn="0001-4273" index="ABDC-A*" category="Management" />
              <JournalCard name="Journal of Marketing" issn="0022-2429" index="ABDC-A*" category="Marketing" />
              <JournalCard name="Strategic Management Journal" issn="0143-2095" index="ABDC-A*" category="Strategy" />
              <JournalCard name="Journal of International Business Studies" issn="0047-2506" index="ABDC-A*" category="International Business" />
              <JournalCard name="Journal of Management Studies" issn="0022-2380" index="ABDC-A" category="Management" />
              <JournalCard name="British Journal of Management" issn="1045-3172" index="ABDC-A" category="Management" />
              <JournalCard name="International Business Review" issn="0969-5931" index="ABDC-A" category="International Business" />
              <JournalCard name="Journal of Business Ethics" issn="0167-4544" index="ABDC-A" category="Ethics" />
              <JournalCard name="Asia Pacific Journal of Management" issn="0217-4561" index="ABDC-B" category="Management" />
            </div>
          </div>

          {/* UGC CARE / Peer Reviewed */}
          <div>
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500 flex items-center justify-center rounded">
                <Users className="w-5 h-5 text-white" />
              </div>
              UGC CARE Listed & Peer Reviewed Journals
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <JournalCard name="Indian Journal of Law and Legal Research" issn="2582-8878" index="UGC CARE" category="Law" />
              <JournalCard name="Journal of Legal Studies and Research" issn="2455-2437" index="UGC CARE" category="Law" />
              <JournalCard name="International Journal of Law and Policy Review" issn="2278-3156" index="Peer Reviewed" category="Law/Policy" />
              <JournalCard name="Journal of Commerce and Management Thought" issn="0975-623X" index="UGC CARE" category="Commerce" />
              <JournalCard name="Indian Journal of Commerce and Management Studies" issn="2229-5674" index="UGC CARE" category="Commerce" />
              <JournalCard name="Asia Pacific Journal of Research" issn="2320-5504" index="Peer Reviewed" category="Multidisciplinary" />
              <JournalCard name="International Journal of Humanities and Social Science Research" issn="2455-2070" index="UGC CARE" category="Social Science" />
              <JournalCard name="Journal of Positive School Psychology" issn="2717-7564" index="Peer Reviewed" category="Psychology" />
              <JournalCard name="Elementary Education Online" issn="1305-3515" index="Peer Reviewed" category="Education" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Publish With Us */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-white mb-6">Why Publish With Us?</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-slate-950" />
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Guaranteed Publication</h3>
              <p className="text-slate-400 text-sm">We ensure your paper gets published in indexed journals</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-950" />
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Complete Support</h3>
              <p className="text-slate-400 text-sm">From writing to publication, we handle everything</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-slate-950" />
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Plagiarism Free</h3>
              <p className="text-slate-400 text-sm">All papers are original and pass Turnitin checks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-slate-950" />
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">High Success Rate</h3>
              <p className="text-slate-400 text-sm">95%+ acceptance rate in indexed journals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-[#C5A059] via-[#d4b06a] to-[#C5A059]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl font-medium text-slate-950 mb-6">
            Ready to Publish Your Research?
          </h2>
          <p className="text-slate-800 text-lg mb-10 max-w-2xl mx-auto">
            Contact us today to discuss your publication requirements. Our team will guide you through the entire process.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-8 py-5 font-bold text-lg transition-colors shadow-xl"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="w-6 h-6" /> Contact on WhatsApp
            </a>
            <a 
              href="mailto:spapublication@gmail.com"
              className="flex items-center gap-3 bg-slate-950 hover:bg-slate-800 text-white px-8 py-5 font-bold text-lg transition-colors shadow-xl"
              data-testid="contact-email-btn"
            >
              <Mail className="w-6 h-6" /> Email Us
            </a>
          </div>

          <div className="bg-white/20 backdrop-blur-sm p-6 inline-block">
            <div className="flex flex-wrap justify-center gap-8 text-slate-950">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span className="font-medium">+91 6284607500</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span className="font-medium">spapublication@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function JournalCard({ name, issn, index, category, impactFactor }) {
  return (
    <div className="bg-white p-5 border border-slate-200 hover:border-[#C5A059] hover:shadow-lg transition-all">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h4 className="font-medium text-slate-900 text-sm leading-tight">{name}</h4>
        <span className={`text-xs px-2 py-1 font-semibold whitespace-nowrap ${
          index.includes('A*') ? 'bg-red-100 text-red-700' :
          index.includes('SCIE') || index.includes('SSCI') ? 'bg-purple-100 text-purple-700' :
          index.includes('SCOPUS') ? 'bg-orange-100 text-orange-700' :
          index.includes('ABDC') ? 'bg-blue-100 text-blue-700' :
          'bg-emerald-100 text-emerald-700'
        }`}>
          {index}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span>ISSN: {issn}</span>
        <span className="bg-slate-100 px-2 py-0.5 rounded">{category}</span>
      </div>
      {impactFactor && (
        <div className="mt-2 text-xs">
          <span className="text-[#C5A059] font-semibold">Impact Factor: {impactFactor}</span>
        </div>
      )}
    </div>
  );
}
